import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from '../common/Modal';
import { useUIStore } from '../../stores/uiStore';
import { assayRequestApi, AssayType, AssayTypesResponse } from '../../services/assayRequestApi';

interface SubmitForAssayModalProps {
  isOpen: boolean;
  onClose: () => void;
  moleculeId: number;
  moleculeIds?: number[];
  projectId: number;
  onSubmitted?: () => void;
}

const CATEGORY_ORDER = [
  'Enzyme Inhibition',
  'Cell-Based',
  'Binding',
  'ADME',
  'Safety',
];

const SubmitForAssayModal: React.FC<SubmitForAssayModalProps> = ({
  isOpen,
  onClose,
  moleculeId,
  moleculeIds,
  projectId,
  onSubmitted,
}) => {
  const showNotification = useUIStore((s) => s.showNotification);

  const [assayTypes, setAssayTypes] = useState<AssayTypesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [targetName, setTargetName] = useState('');
  const [notes, setNotes] = useState('');
  const [runDate, setRunDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Target name suggestions from previously used targets
  const [targetSuggestions, setTargetSuggestions] = useState<string[]>([]);
  const [showTargetSuggestions, setShowTargetSuggestions] = useState(false);
  const targetInputRef = useRef<HTMLInputElement>(null);

  // Fetch assay types on open
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    assayRequestApi.fetchAssayTypes()
      .then((data) => {
        setAssayTypes(data);
        // Extract unique target names from recent requests for autocomplete
        assayRequestApi.fetchAssayRequests({ project_id: projectId })
          .then((resp) => {
            const targets = Array.from(new Set(
              resp.requests
                .map((r) => r.target_name)
                .filter((t): t is string => !!t)
            )).sort();
            setTargetSuggestions(targets);
          })
          .catch(() => {});
      })
      .catch(() => {
        showNotification('error', 'Failed to load assay types');
      })
      .finally(() => setLoading(false));
  }, [isOpen, projectId, showNotification]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedTypeId(null);
      setTargetName('');
      setNotes('');
      setDropdownOpen(false);
      setShowTargetSuggestions(false);
    }
  }, [isOpen]);

  const selectedType = useMemo(() => {
    if (!assayTypes || !selectedTypeId) return null;
    return assayTypes.types.find((t) => t.id === selectedTypeId) || null;
  }, [assayTypes, selectedTypeId]);

  const sortedCategories = useMemo(() => {
    if (!assayTypes) return [];
    const cats = Object.keys(assayTypes.by_category);
    return cats.sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [assayTypes]);

  const filteredSuggestions = useMemo(() => {
    if (!targetName.trim()) return targetSuggestions;
    const lower = targetName.toLowerCase();
    return targetSuggestions.filter((t) => t.toLowerCase().includes(lower));
  }, [targetName, targetSuggestions]);

  const allMoleculeIds = useMemo(() => {
    if (moleculeIds && moleculeIds.length > 0) return moleculeIds;
    return [moleculeId];
  }, [moleculeId, moleculeIds]);

  const handleSubmit = async () => {
    if (!selectedTypeId) return;
    setSubmitting(true);

    try {
      await assayRequestApi.createAssayRun({
        molecule_ids: allMoleculeIds,
        project_id: projectId,
        assay_type_id: selectedTypeId,
        run_date: runDate,
        notes: [targetName.trim() ? `Target: ${targetName.trim()}` : '', notes.trim()].filter(Boolean).join('\n') || undefined,
      });

      const typeName = selectedType?.name || 'assay';
      if (allMoleculeIds.length === 1) {
        showNotification('success', `Submitted compound for ${typeName} assay`);
      } else {
        showNotification('success', `Created one ${typeName} run with ${allMoleculeIds.length} compounds`);
      }

      onSubmitted?.();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showNotification('error', typeof detail === 'string' ? detail : 'Failed to submit assay request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Submit for Assay" size="md">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div
            className="w-5 h-5 border-2 rounded-full animate-spin"
            style={{ borderColor: 'var(--primary-600)', borderTopColor: 'transparent' }}
          />
          <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            Loading assay types...
          </span>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Batch count indicator */}
          {allMoleculeIds.length > 1 && (
            <div
              className="text-xs px-3 py-2 rounded-md"
              style={{ backgroundColor: 'rgba(13,148,136,0.08)', color: 'var(--primary-600)' }}
            >
              Submitting {allMoleculeIds.length} compounds for assay
            </div>
          )}

          {/* Assay Type Dropdown */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Assay Type <span style={{ color: 'var(--error-500)' }}>*</span>
            </label>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full text-left px-3 py-2 text-sm rounded-md"
                style={{
                  backgroundColor: 'var(--content-bg)',
                  border: '1px solid var(--card-border)',
                  color: selectedType ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                <div className="flex items-center justify-between">
                  <span>
                    {selectedType ? (
                      <>
                        {selectedType.name}
                        {selectedType.typical_units && (
                          <span className="ml-2" style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                            {selectedType.typical_units}
                          </span>
                        )}
                      </>
                    ) : (
                      'Select assay type...'
                    )}
                  </span>
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: 'var(--text-muted)', transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {dropdownOpen && assayTypes && (
                <div
                  className="absolute left-0 right-0 mt-1 rounded-md overflow-auto"
                  style={{
                    zIndex: 50,
                    maxHeight: 320,
                    backgroundColor: 'var(--dropdown-bg, var(--card-bg))',
                    boxShadow: 'var(--dropdown-shadow, 0 4px 20px rgba(0,0,0,0.15))',
                    border: '1px solid var(--card-border)',
                  }}
                >
                  {sortedCategories.map((cat) => (
                    <div key={cat}>
                      {/* Category header — non-selectable */}
                      <div
                        className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider"
                        style={{
                          color: 'var(--text-muted)',
                          backgroundColor: 'var(--hover-bg)',
                          borderBottom: '1px solid var(--card-border)',
                          position: 'sticky',
                          top: 0,
                        }}
                      >
                        {cat}
                      </div>
                      {assayTypes.by_category[cat]?.map((at) => (
                        <button
                          key={at.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm flex items-center justify-between"
                          style={{
                            color: at.id === selectedTypeId ? 'var(--primary-600)' : 'var(--text-primary)',
                            backgroundColor: at.id === selectedTypeId ? 'rgba(13,148,136,0.08)' : 'transparent',
                            borderBottom: '1px solid var(--card-border)',
                          }}
                          onMouseEnter={(e) => {
                            if (at.id !== selectedTypeId) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                          }}
                          onMouseLeave={(e) => {
                            if (at.id !== selectedTypeId) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onClick={() => {
                            setSelectedTypeId(at.id);
                            setDropdownOpen(false);
                          }}
                        >
                          <span>{at.name}</span>
                          {at.typical_units && (
                            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{at.typical_units}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Planned run date *</label>
            <input type="date" value={runDate} onChange={(event) => setRunDate(event.target.value)} className="w-full px-3 py-2 text-sm rounded-md border" style={{ backgroundColor: 'var(--content-bg)', borderColor: 'var(--card-border)', color: 'var(--text-primary)' }} />
          </div>

          {/* Target Name */}
          <div style={{ position: 'relative' }}>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Target Name
            </label>
            <input
              ref={targetInputRef}
              type="text"
              value={targetName}
              onChange={(e) => setTargetName(e.target.value)}
              onFocus={() => setShowTargetSuggestions(true)}
              onBlur={() => setTimeout(() => setShowTargetSuggestions(false), 150)}
              placeholder="e.g. EGFR, HER2, p38 MAPK"
              className="w-full px-3 py-2 text-sm rounded-md"
              style={{
                backgroundColor: 'var(--content-bg)',
                border: '1px solid var(--card-border)',
                color: 'var(--text-primary)',
              }}
            />
            {showTargetSuggestions && filteredSuggestions.length > 0 && (
              <div
                className="absolute left-0 right-0 mt-1 rounded-md overflow-auto"
                style={{
                  zIndex: 50,
                  maxHeight: 160,
                  backgroundColor: 'var(--dropdown-bg, var(--card-bg))',
                  boxShadow: 'var(--dropdown-shadow, 0 4px 20px rgba(0,0,0,0.15))',
                  border: '1px solid var(--card-border)',
                }}
              >
                {filteredSuggestions.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-sm"
                    style={{ color: 'var(--text-primary)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setTargetName(t);
                      setShowTargetSuggestions(false);
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instructions for the biologist (optional)"
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md resize-none"
              style={{
                backgroundColor: 'var(--content-bg)',
                border: '1px solid var(--card-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md"
              style={{
                color: 'var(--text-secondary)',
                border: '1px solid var(--card-border)',
                backgroundColor: 'transparent',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedTypeId || submitting}
              className="px-4 py-2 text-sm font-medium rounded-md text-white"
              style={{
                backgroundColor: !selectedTypeId || submitting ? 'var(--gray-400)' : '#0D9488',
                cursor: !selectedTypeId || submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                `Submit${allMoleculeIds.length > 1 ? ` (${allMoleculeIds.length})` : ''}`
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SubmitForAssayModal;
